// PM-Dashboard-PANEL (Meister-Uebersicht) — Phase 1: Kennzahlen aus GET /api/pm/dashboard,
// das portal-seitig aus den vorhandenen Seam-Reads rechnet (kein Kontrakt-Eingriff, par. 4a).
//
// Reine INHALTS-Komponente ohne eigene Navigation: sie lebt als ein-/ausklappbare Sektion
// OBEN auf dem Meister-Home (HomeScreen) — Dashboard und Startseite sind EIN Screen, der
// Nutzer blendet die Zahlen bei Bedarf aus (Zustand wird je Geraet gemerkt). Kein Fetch im
// zugeklappten Zustand (Panel wird erst beim Aufklappen gemountet).
//
// Folgt bewusst dem LAGER-Dashboard-Idiom (components/lager/dashboard), ist aber PM-EIGEN:
// eigene Komponenten, eigene i18n-Keys, Drill-down in die BESTEHENDEN PM-Listen (Freigaben/
// Auftragsbestand) statt eines eigenen Filtermodells. Jede Kennzahl traegt `source` vom
// Backend; bei 'mock' zeigt das amber Badge, dass die Zahl (noch) nicht aus SAP kommt —
// dieselbe Konvention wie im Lager (HANDOVER-Regel: Mock nie unmarkiert anzeigen).
//
// Ein gemeinsames Dashboard-Kit in core/ui ist bewusst NICHT Teil dieser Phase
// (Koordination Taras; siehe improvement-backlog D7).
import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Inbox, AlertTriangle, Bell } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { getDashboard } from '../api';
import { Card, Row } from './ui';
import WeekStrip from './WeekStrip';

function DashboardPanel({ me, user, onOpenList }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  // seq-Guard wie OrdersList: nur die Antwort des juengsten Requests setzt den State.
  const seq = useRef(0);

  const load = () => {
    const mySeq = ++seq.current;
    setLoadError(false);
    getDashboard(user, me.activePlant, me.profile.id)
      .then((res) => { if (seq.current === mySeq) setData(res); })
      .catch(() => { if (seq.current === mySeq) setLoadError(true); });
  };
  // Neu laden bei Werks-/Rollenwechsel (me aendert sich).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [user, me]);

  const mockBadge = isDarkMode
    ? 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300 ml-2'
    : 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 ml-2';

  return (
    <div className="space-y-3">
      {loadError ? (
        <div className="py-10 text-center space-y-3">
          <p className={isDarkMode ? 'text-red-300' : 'text-red-600'}>{t('pm.common.loadError')}</p>
          <button onClick={load} className={`text-sm font-medium underline ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
            {t('pm.common.retry')}
          </button>
        </div>
      ) : !data ? (
        <p className={`py-10 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.common.loading')}</p>
      ) : (
        <>
          {/* KPI-Karten — Drill-down in die bestehenden Listen (Lager-Muster). */}
          <div className="grid grid-cols-2 gap-3">
            <KpiCard
              Icon={CheckCircle2} accent="amber" label={t('pm.dash.pendingApprovals')}
              kpi={data.pendingApprovals} mockBadge={mockBadge}
              onClick={() => onOpenList('approvals')} hint={t('pm.dash.clickHint')}
            />
            <KpiCard
              Icon={Inbox} accent="blue" label={t('pm.dash.openOrders')}
              kpi={data.openOrders} mockBadge={mockBadge}
              onClick={() => onOpenList('backlog')} hint={t('pm.dash.clickHint')}
            />
            <KpiCard
              Icon={AlertTriangle} accent="rose" label={t('pm.dash.overdue')}
              kpi={data.overdueOrders} mockBadge={mockBadge}
              onClick={() => onOpenList('backlog')} hint={t('pm.dash.clickHint')}
            />
            {/* Meldungen: (noch) keine eigene Listen-Sicht -> nicht klickbar (Phase 1). */}
            <KpiCard
              Icon={Bell} accent="violet" label={t('pm.dash.openNotifications')}
              kpi={data.openNotifications} mockBadge={mockBadge}
            />
          </div>

          {/* Wochenvorschau (werksweit): Ueberfaellig zuerst, dann heute + 6 Tage. */}
          <WeekStrip title={t('pm.week.next7')} week={data.weekAhead} onClick={() => onOpenList('backlog')} />

          {/* Aufschluesselungen: Status + Auftragsart (Codes uebersetzen, roher Code als Fallback). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <BreakdownCard
              title={t('pm.dash.byStatus')} entries={data.ordersByStatus?.data}
              labelFor={(code) => translated(t, `pm.status.${code}`, code)}
              source={data.ordersByStatus?.source} mockBadge={mockBadge}
              emptyText={t('pm.dash.empty')} mockLabel={t('pm.dash.mockBadge')}
            />
            <BreakdownCard
              title={t('pm.dash.byType')} entries={data.ordersByType?.data}
              labelFor={(code) => `${translated(t, `pm.orderType.${code}`, code)} (${code})`}
              source={data.ordersByType?.source} mockBadge={mockBadge}
              emptyText={t('pm.dash.empty')} mockLabel={t('pm.dash.mockBadge')}
            />
          </div>

          {/* Neueste Meldungen (top 3, neueste zuerst). */}
          <Card className="space-y-2">
            <h3 className={`text-sm font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
              {t('pm.dash.recentNotifications')}
              {data.recentNotifications?.source === 'mock' && (
                <span className={mockBadge}>{t('pm.dash.mockBadge')}</span>
              )}
            </h3>
            {(data.recentNotifications?.data || []).length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('pm.dash.empty')}</p>
            ) : (
              data.recentNotifications.data.map((n) => (
                <div key={n.notificationId} className="flex justify-between gap-3 text-sm">
                  <span className={`truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{n.description}</span>
                  <span className={`shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                    {n.equipmentId} · {(n.createdAt || '').slice(0, 10)}
                  </span>
                </div>
              ))
            )}
          </Card>
        </>
      )}
    </div>
  );
}

/** t() faellt auf den Key selbst zurueck — dann den rohen Code zeigen (StatusBadge-Muster). */
function translated(t, key, fallback) {
  const label = t(key);
  return label === key ? fallback : label;
}

// KPI-Karte — Akzentfarbe je Kennzahl, klickbar = Drill-down (Lager-Idiom, PM-eigen).
function KpiCard({ Icon, accent, label, kpi, mockBadge, onClick, hint }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const ACCENTS = {
    amber:  isDarkMode ? 'bg-amber-500/20 text-amber-300'   : 'bg-amber-100 text-amber-600',
    blue:   isDarkMode ? 'bg-blue-500/20 text-blue-300'     : 'bg-blue-100 text-blue-600',
    rose:   isDarkMode ? 'bg-rose-500/20 text-rose-300'     : 'bg-rose-100 text-rose-600',
    violet: isDarkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-600',
  };
  const VALUES = {
    amber:  isDarkMode ? 'text-amber-300'  : 'text-amber-700',
    blue:   isDarkMode ? 'text-blue-200'   : 'text-blue-700',
    rose:   isDarkMode ? 'text-rose-300'   : 'text-rose-700',
    violet: isDarkMode ? 'text-violet-200' : 'text-violet-700',
  };
  const clickable = typeof onClick === 'function';
  const inner = (
    <Card className={`h-full ${clickable ? 'transition-all hover:shadow-md' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`text-xs font-medium ${isDarkMode ? 'text-white/55' : 'text-slate-500'}`}>
            {label}
            {kpi?.source === 'mock' && <span className={mockBadge}>{t('pm.dash.mockBadge')}</span>}
          </div>
          <div className={`text-2xl font-bold mt-0.5 ${VALUES[accent]}`}>{kpi?.count ?? 0}</div>
        </div>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${ACCENTS[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {clickable && (
        <div className={`text-[10px] mt-1 ${isDarkMode ? 'text-white/25' : 'text-slate-300'}`}>{hint}</div>
      )}
    </Card>
  );
  return clickable
    ? <button onClick={onClick} className="text-left w-full">{inner}</button>
    : inner;
}

// Aufschluesselungs-Karte: Code -> Anzahl als Zeilen (Row aus dem PM-UI-Kit).
function BreakdownCard({ title, entries, labelFor, source, mockBadge, emptyText, mockLabel }) {
  const { isDarkMode } = useTheme();
  const items = Object.entries(entries || {});
  return (
    <Card className="space-y-2">
      <h3 className={`text-sm font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
        {title}
        {source === 'mock' && <span className={mockBadge}>{mockLabel}</span>}
      </h3>
      {items.length === 0 ? (
        <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{emptyText}</p>
      ) : (
        items.map(([code, count]) => <Row key={code} label={labelFor(code)} value={count} />)
      )}
    </Card>
  );
}

export default DashboardPanel;
